import React, { useMemo } from 'react';
import { Dot } from '@ygtechnologies/rubus-ui';
import { Thumbnail, classnames } from '@ygtechnologies/bygr-frontend-component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faImage } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';

import card from '../components/RoomOptionTwoTierList/RoomOptionCards.module.scss';
import { ConfiguratorPresetGroupDto } from '@/services/configurator';
import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';
import { useImageUrls } from '../../useImageUrls';

interface StyleGroupCardProps {
    styleGroup: ConfiguratorPresetGroupDto;
    onClick: () => void;
}

// A style group rendered as a two-column card (image left, info right) in the centered card grid —
// the card equivalent of `StyleGroupRow`. Clicking opens the style-selection modal.
export const StyleGroupCard: React.FC<StyleGroupCardProps> = ({ styleGroup, onClick }) => {
    const { getOptionPresetImageUrl } = useImageUrls();
    const pickedStyle = styleGroup.styles.find((s) => s.isPicked) ?? null;
    const selected = !!pickedStyle;
    const imageId = pickedStyle?.images[0]?.id ?? null;
    const src = useMemo(
        () => (imageId != null ? getOptionPresetImageUrl(imageId) : undefined),
        [imageId, getOptionPresetImageUrl]
    );

    return (
        <button type="button" className={card.card} onClick={onClick}>
            <div className={card.media}>
                <Thumbnail
                    className={card.thumbnail}
                    src={src}
                    alt={pickedStyle?.name ?? styleGroup.name}
                    fallback={
                        <div className={classnames(card.thumbnail, card.thumbnailFallback)}>
                            <FontAwesomeIcon icon={faImage} />
                        </div>
                    }
                />
            </div>
            <div className={card.info}>
                <div className={card.topLine}>
                    <span className={card.name} title={styleGroup.name}>
                        {styleGroup.name}
                    </span>
                    <FontAwesomeIcon className={card.chevron} icon={faChevronRight} />
                </div>
                <div className={card.selection} title={pickedStyle?.name ?? undefined}>
                    <Dot variant={selected ? 'primary' : 'warning'} />
                    <span className={card.selectionName}>
                        {selected ? pickedStyle?.name : <Trans>None selected</Trans>}
                    </span>
                </div>
                {pickedStyle && (
                    <div className={card.price}>
                        <BuyerCurrencyDisplay value={pickedStyle.price} />
                    </div>
                )}
            </div>
        </button>
    );
};
